/**
 * @fileoverview Main Operator Administration Panel for Memorama (Memory 1 vs 1).
 * Allows stadium operators to control screen projections, configure rounds (pairs count, tie-break, timers),
 * edit player names on the fly, monitor real-time telemetries at 100ms, execute emergency score adjustments,
 * manage theme catalogs, and review matches history.
 * 
 * @module components/Admin/Games/Memory/Memory
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import { PremiumQR } from '../../../Commons/PremiumQR';
import { MemoryThemeModal } from './MemoryThemeModal';

import {
    GetThemes,
    GetConfig,
    SaveConfig,
    GetMatchHistory,
    DeleteTheme,
    CloneTheme,
    ShowCover,
    ShowGameScene,
    StartMatch,
    FinishMatchEarly,
    ResetMatch,
    GetMatchState,
    UpdatePlayerNames,
    AdjustScore,
    ClearPlayers,
    ChangeAccessPin,
    encodeAccessCode
} from '../../../../utilities/BL/Memory';

import {
    ConnectWebSocketForMemoryAdmin,
    DisconnectWebSocketForMemoryAdmin
} from '../../../../utilities/WS/Admin/MemoryWS';

/**
 * Resolves media path into an absolute URL for image previews.
 * 
 * @param {string} mediaPath - Relative or absolute media URL.
 * @returns {string} Resolved preview URL.
 */
const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/').replace('/api/', '/').replace('/api', '/');
    return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * Memorama Operator Administration Component.
 * 
 * @function Memory
 * @param {Object} props - Component properties.
 * @param {Function} props.getOutOfTheDynamic - Callback to return to dashboard.
 * @param {string|number} [props.roomId] - Room identifier GUID or ID.
 * @returns {React.ReactElement} Admin panel markup.
 */
export const Memory = ({ getOutOfTheDynamic, roomId: propRoomId }) => {
    const { showMessage } = useFloatMessage();

    // Contextual Room ID detection
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);
    const activeRoomId = propRoomId ||
        searchParams.get('roomId') ||
        (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') ||
        '';

    // Loading & Network state
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
    const [lastRefreshTimestampString, setLastRefreshTimestampString] = useState('');
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('connecting');

    // Themes & Configurations
    const [availableThemesList, setAvailableThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState(0);
    const [activeThemeObject, setActiveThemeObject] = useState(null);
    const [roomConfigState, setRoomConfigState] = useState({
        roundDurationSeconds: 60,
        pairsCount: 6,
        tieBreakMode: 'least_flips', // 'least_flips' | 'pure_tie' | 'fastest_match'
        previewTimeSeconds: 3,
        hideUnmatchedDelayMs: 800
    });
    const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);

    // Live Game State & Telemetry
    const [currentMemoryGameState, setCurrentMemoryGameState] = useState({
        phase: 'idle', // 'idle' | 'cover' | 'game_ready' | 'countdown' | 'preview' | 'playing' | 'finished'
        roundDurationSeconds: 60,
        remainingSeconds: 60,
        pairsCount: 6,
        previewTimeSeconds: 3,
        hideUnmatchedDelayMs: 800,
        tieBreakMode: 'least_flips',
        winner: null,
        winningReason: null,
        countdownSeconds: 3,
        player1: {
            name: 'Jugador 1',
            score: 0,
            totalFlips: 0,
            isConnected: false,
            color: '#fbbf24',
            avatarUrl: '',
            winnerAvatarUrl: '',
            finished: false
        },
        player2: {
            name: 'Jugador 2',
            score: 0,
            totalFlips: 0,
            isConnected: false,
            color: '#60a5fa',
            avatarUrl: '',
            winnerAvatarUrl: '',
            finished: false
        }
    });

    // Countdown active count
    const [countdownRemaining, setCountdownRemaining] = useState(null);

    // On-the-fly Player Names input states
    const [player1LiveName, setPlayer1LiveName] = useState('Jugador 1');
    const [player2LiveName, setPlayer2LiveName] = useState('Jugador 2');
    const [isUpdatingNames, setIsUpdatingNames] = useState(false);

    // Matches History
    const [matchesHistoryList, setMatchesHistoryList] = useState([]);

    // Modals
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmModalConfiguration, setConfirmModalConfiguration] = useState({
        message: '',
        actionToExecute: null,
        yesButtonText: 'Sí, confirmar',
        noButtonText: 'Cancelar'
    });
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [themeToEditObject, setThemeToEditObject] = useState(null);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [fanJoinQrUrl, setFanJoinQrUrl] = useState('');
    const [screenTargetDisplayUrl, setScreenTargetDisplayUrl] = useState('');
    const [accessPin, setAccessPin] = useState('');

    /**
     * Normalizes incoming telemetry / match state into uniform structure.
     */
    const updateMemoryStateFromPacket = useCallback((data) => {
        if (!data || typeof data !== 'object') return;

        const p1 = data.player1 || data.Player1 || {};
        const p2 = data.player2 || data.Player2 || {};

        const phase = data.phase || data.Phase || data.state || data.State;
        const rem = data.remainingSeconds ?? data.RemainingSeconds ?? (typeof data.remainingMs === 'number'
            ? Math.max(0, Math.round((data.remainingMs / 1000) * 10) / 10)
            : null);

        const dur = data.roundDurationSeconds ?? data.RoundDurationSeconds ?? data.roundDuration ?? data.RoundDuration;
        const pairs = data.pairsCount ?? data.PairsCount ?? data.configuredPairs ?? data.ConfiguredPairs;
        const winner = data.winner ?? data.Winner;
        const incomingPin = data.accessPin ?? data.AccessPin;
        if (incomingPin) setAccessPin(String(incomingPin));
        const winningReason = data.winningReason ?? data.WinningReason ?? data.reason ?? data.Reason;

        setCurrentMemoryGameState((prev) => {
            const nextState = {
                ...prev,
                phase: phase !== undefined ? phase : prev.phase,
                remainingSeconds: rem !== null && rem !== undefined ? rem : prev.remainingSeconds,
                roundDurationSeconds: dur !== undefined ? Number(dur) : prev.roundDurationSeconds,
                pairsCount: pairs !== undefined ? Number(pairs) : prev.pairsCount,
                winner: winner !== undefined ? winner : prev.winner,
                winningReason: winningReason !== undefined ? winningReason : prev.winningReason,
                player1: {
                    ...prev.player1,
                    name: p1.name ?? p1.Name ?? prev.player1.name,
                    score: p1.score ?? p1.Score ?? data.scoreJ1 ?? data.ScoreJ1 ?? prev.player1.score,
                    totalFlips: p1.totalFlips ?? p1.TotalFlips ?? data.totalFlipsJ1 ?? data.TotalFlipsJ1 ?? prev.player1.totalFlips,
                    isConnected: p1.isConnected ?? p1.IsConnected ?? prev.player1.isConnected,
                    color: p1.color ?? p1.Color ?? prev.player1.color,
                    avatarUrl: p1.avatarUrl ?? p1.AvatarUrl ?? prev.player1.avatarUrl,
                    winnerAvatarUrl: p1.winnerAvatarUrl ?? p1.WinnerAvatarUrl ?? prev.player1.winnerAvatarUrl,
                    finished: p1.finished ?? p1.Finished ?? prev.player1.finished
                },
                player2: {
                    ...prev.player2,
                    name: p2.name ?? p2.Name ?? prev.player2.name,
                    score: p2.score ?? p2.Score ?? data.scoreJ2 ?? data.ScoreJ2 ?? prev.player2.score,
                    totalFlips: p2.totalFlips ?? p2.TotalFlips ?? data.totalFlipsJ2 ?? data.TotalFlipsJ2 ?? prev.player2.totalFlips,
                    isConnected: p2.isConnected ?? p2.IsConnected ?? prev.player2.isConnected,
                    color: p2.color ?? p2.Color ?? prev.player2.color,
                    avatarUrl: p2.avatarUrl ?? p2.AvatarUrl ?? prev.player2.avatarUrl,
                    winnerAvatarUrl: p2.winnerAvatarUrl ?? p2.WinnerAvatarUrl ?? prev.player2.winnerAvatarUrl,
                    finished: p2.finished ?? p2.Finished ?? prev.player2.finished
                }
            };

            return nextState;
        });
    }, []);

    /**
     * Handles incoming WebSocket messages for the operator.
     */
    const handleWebSocketMessageReceived = useCallback((receivedEnvelope) => {
        if (!receivedEnvelope || typeof receivedEnvelope !== 'object') return;

        const rawType = String(receivedEnvelope.type || receivedEnvelope.Type || '').toLowerCase();
        const payloadData = receivedEnvelope.payload !== undefined ? receivedEnvelope.payload : receivedEnvelope.data || receivedEnvelope;

        switch (rawType) {
            case 'memory_telemetry':
                updateMemoryStateFromPacket(payloadData);
                break;

            case 'memory_sync_state':
            case 'memory_state':
            case 'memory_cover_shown':
            case 'memory_cover':
            case 'memory_game_ready':
            case 'memory_game_shown':
                setCountdownRemaining(null);
                updateMemoryStateFromPacket(payloadData);
                break;

            case 'memory_countdown_started':
            case 'memory_countdown': {
                const countVal = payloadData?.countdown ?? payloadData?.secondsRemaining ?? payloadData?.count ?? payloadData?.step ?? 3;
                setCountdownRemaining(countVal);
                setCurrentMemoryGameState((prev) => ({
                    ...prev,
                    phase: 'countdown',
                    winner: null,
                    winningReason: '',
                    player1: { ...prev.player1, score: 0, totalFlips: 0, finished: false },
                    player2: { ...prev.player2, score: 0, totalFlips: 0, finished: false },
                    remainingSeconds: prev.roundDurationSeconds || 60
                }));
                break;
            }

            case 'memory_preview':
            case 'memory_preview_start':
                setCountdownRemaining(null);
                setCurrentMemoryGameState((prev) => ({
                    ...prev,
                    phase: 'preview',
                    winner: null
                }));
                break;

            case 'memory_preview_ended':
            case 'memory_playing_started':
            case 'memory_start':
            case 'memory_game_start':
                setCountdownRemaining(null);
                setCurrentMemoryGameState((prev) => ({
                    ...prev,
                    phase: 'playing',
                    winner: null
                }));
                break;

            case 'memory_card_flipped':
            case 'memory_match_success':
            case 'match_success':
            case 'memory_match_failed':
            case 'match_failed':
            case 'memory_cards_unflipped':
            case 'memory_all_pairs_matched':
            case 'memory_score_adjusted':
                if (payloadData?.matchState || payloadData?.state) {
                    updateMemoryStateFromPacket(payloadData.matchState || payloadData.state);
                } else {
                    updateMemoryStateFromPacket(payloadData);
                }
                break;

            case 'memory_finished':
            case 'memory_game_over':
            case 'memory_game_finished': {
                setCountdownRemaining(null);
                const rawWinner = payloadData?.winner || receivedEnvelope?.winner || 'tie';
                const finalWinner = (rawWinner === 'player1' || rawWinner === 'J1') ? 'Jugador 1'
                    : ((rawWinner === 'player2' || rawWinner === 'J2') ? 'Jugador 2'
                    : 'Empate');

                setCurrentMemoryGameState((prev) => ({
                    ...prev,
                    phase: 'finished',
                    winner: finalWinner,
                    remainingSeconds: 0
                }));
                loadMatchesHistory();
                break;
            }

            case 'memory_names_updated':
            case 'memory_update_names': {
                const p1Name = payloadData?.player1Name || payloadData?.Player1Name;
                const p2Name = payloadData?.player2Name || payloadData?.Player2Name;
                if (p1Name) setPlayer1LiveName(p1Name);
                if (p2Name) setPlayer2LiveName(p2Name);
                updateMemoryStateFromPacket(payloadData);
                break;
            }

            case 'memory_match_reset':
                setCountdownRemaining(null);
                setCurrentMemoryGameState((prev) => ({
                    ...prev,
                    phase: 'game_ready',
                    winner: null,
                    player1: { ...prev.player1, score: 0, totalFlips: 0, finished: false },
                    player2: { ...prev.player2, score: 0, totalFlips: 0, finished: false },
                    remainingSeconds: prev.roundDurationSeconds || 60
                }));
                break;

            case 'memory_player_joined':
            case 'memory_player_left':
            case 'leave_game':
            case 'memory_player_disconnected':
                updateMemoryStateFromPacket(payloadData);
                break;

            default:
                break;
        }
    }, [updateMemoryStateFromPacket]);

    /**
     * Initializes data and WebSocket connection on mount.
     */
    useEffect(() => {
        loadAllInitialAdminData();

        ConnectWebSocketForMemoryAdmin(
            handleWebSocketMessageReceived,
            (newStatus) => setWebSocketConnectionStatus(newStatus),
            activeRoomId
        );

        return () => {
            DisconnectWebSocketForMemoryAdmin();
        };
    }, [activeRoomId, handleWebSocketMessageReceived]);

    /**
     * Fetches all themes, room configuration, real-time snapshot and match history.
     */
    const loadAllInitialAdminData = async () => {
        setIsLoadingInitialData(true);
        try {
            // 1. Fetch Themes Catalog
            const themesResponse = await GetThemes(activeRoomId);
            if (themesResponse.success && Array.isArray(themesResponse.data)) {
                setAvailableThemesList(themesResponse.data);
            }

            // 2. Fetch Room Configuration
            const configResponse = await GetConfig(activeRoomId);
            if (configResponse.success && configResponse.data) {
                const configData = configResponse.data;
                setRoomConfigState({
                    roundDurationSeconds: configData.roundDurationSeconds || 60,
                    pairsCount: configData.pairsCount || 6,
                    tieBreakMode: configData.tieBreakMode || 'least_flips',
                    previewTimeSeconds: configData.previewTimeSeconds !== undefined ? configData.previewTimeSeconds : 3,
                    hideUnmatchedDelayMs: configData.hideUnmatchedDelayMs || 800
                });

                if (configData.activeThemeId) {
                    setSelectedThemeId(configData.activeThemeId);
                } else if (themesResponse.success && themesResponse.data?.length > 0) {
                    setSelectedThemeId(themesResponse.data[0].id);
                }

                if (configData.activeTheme) {
                    setActiveThemeObject(configData.activeTheme);
                    if (configData.activeTheme.player1Name) setPlayer1LiveName(configData.activeTheme.player1Name);
                    if (configData.activeTheme.player2Name) setPlayer2LiveName(configData.activeTheme.player2Name);
                }
            }

            // 3. Fetch Real-time Engine Snapshot
            const realtimeResponse = await GetMatchState(activeRoomId);
            if (realtimeResponse.success && realtimeResponse.data) {
                updateMemoryStateFromPacket(realtimeResponse.data);
                if (realtimeResponse.data.player1?.name) setPlayer1LiveName(realtimeResponse.data.player1.name);
                if (realtimeResponse.data.player2?.name) setPlayer2LiveName(realtimeResponse.data.player2.name);
                if (realtimeResponse.data.theme) {
                    setActiveThemeObject(realtimeResponse.data.theme);
                }
            }

            // 4. Fetch History
            await loadMatchesHistory();
        } catch (initializationError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar los datos de administración de Memorama.' });
        } finally {
            setIsLoadingInitialData(false);
            setLastRefreshTimestampString(
                new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            );
        }
    };

    /**
     * Loads the match history log from backend.
     */
    const loadMatchesHistory = async () => {
        if (!activeRoomId) return;
        try {
            const historyResponse = await GetMatchHistory(activeRoomId, 50);
            if (historyResponse.success && Array.isArray(historyResponse.data)) {
                setMatchesHistoryList(historyResponse.data);
            }
        } catch (historyException) {
            // Non-blocking log fetch
        }
    };

    /**
     * Validates whether a theme is actively selected before proceeding with projections or game start.
     * 
     * @returns {boolean} True if a theme is selected, false otherwise.
     */
    const checkThemeSelected = () => {
        if (!selectedThemeId || Number(selectedThemeId) === 0 || selectedThemeId === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona un tema visual para continuar' });
            return false;
        }
        return true;
    };

    /**
     * Handles active theme selection change and persists configuration.
     */
    const handleActiveThemeSelectionChange = async (event) => {
        const rawValue = event.target.value;
        const newlySelectedId = rawValue ? Number(rawValue) : 0;
        setSelectedThemeId(newlySelectedId);

        const foundTheme = availableThemesList.find((themeItem) => themeItem.id === newlySelectedId);
        setActiveThemeObject(foundTheme || null);

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando tema activo...' });
        const saveConfigResponse = await SaveConfig({
            roomId: activeRoomId,
            activeThemeId: newlySelectedId > 0 ? newlySelectedId : null,
            roundDurationSeconds: roomConfigState.roundDurationSeconds,
            pairsCount: roomConfigState.pairsCount,
            tieBreakMode: roomConfigState.tieBreakMode,
            previewTimeSeconds: roomConfigState.previewTimeSeconds,
            hideUnmatchedDelayMs: roomConfigState.hideUnmatchedDelayMs
        }, activeRoomId);

        if (saveConfigResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema activo actualizado correctamente.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: saveConfigResponse.message || 'Error al actualizar el tema activo.' });
        }
    };

    /**
     * Updates configuration parameters (duration, pairs, tie-break, animation delays).
     */
    const handleConfigParameterChange = async (parameterName, parameterValue) => {
        const updatedConfig = {
            ...roomConfigState,
            [parameterName]: parameterValue
        };
        setRoomConfigState(updatedConfig);

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando configuración...' });
        const saveConfigResponse = await SaveConfig({
            roomId: activeRoomId,
            activeThemeId: selectedThemeId > 0 ? selectedThemeId : null,
            roundDurationSeconds: updatedConfig.roundDurationSeconds,
            pairsCount: updatedConfig.pairsCount,
            tieBreakMode: updatedConfig.tieBreakMode,
            previewTimeSeconds: updatedConfig.previewTimeSeconds,
            hideUnmatchedDelayMs: updatedConfig.hideUnmatchedDelayMs
        }, activeRoomId);

        if (saveConfigResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Configuración guardada.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: saveConfigResponse.message || 'Error al guardar la configuración.' });
        }
    };

    // ==========================================
    // PROJECTION ACTIONS
    // ==========================================

    const handleShowCoverAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Portada de Memorama...' });
        const coverResponse = await ShowCover(activeRoomId, selectedThemeId);
        if (coverResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada emitida a pantalla gigante.' });
            if (coverResponse.data) updateMemoryStateFromPacket(coverResponse.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: coverResponse.message || 'Error al emitir portada.' });
        }
    };

    const handleShowGameSceneAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Escenario de Memorama 1 vs 1...' });
        const gameResponse = await ShowGameScene(activeRoomId, selectedThemeId);
        if (gameResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Escenario de juego proyectado en pantalla.' });
            if (gameResponse.data) updateMemoryStateFromPacket(gameResponse.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: gameResponse.message || 'Error al proyectar escenario.' });
        }
    };

    const handleOpenQrModal = async () => {
        if (!checkThemeSelected()) return;
        const currentHostname = window.location.hostname;
        const currentPort = window.location.port ? `:${window.location.port}` : '';
        const currentProtocol = window.location.protocol;
        const roomQuery = activeRoomId ? `?roomId=${encodeURIComponent(activeRoomId)}` : '';

        let currentPin = accessPin;
        if (!currentPin && activeRoomId) {
            try {
                const st = await GetMatchState(activeRoomId);
                if (st.success && st.data?.accessPin) {
                    currentPin = st.data.accessPin;
                    setAccessPin(currentPin);
                }
            } catch (e) {}
        }

        const token = encodeAccessCode(activeRoomId, currentPin);
        const fanUrl = `${currentProtocol}//${currentHostname}${currentPort}/memory?t=${token}`;
        const screenUrl = `${currentProtocol}//${currentHostname}${currentPort}/screen${roomQuery}`;

        setFanJoinQrUrl(fanUrl);
        setScreenTargetDisplayUrl(screenUrl);
        setIsQrModalOpen(true);
    };

    const handleConfirmClearPlayers = () => {
        setConfirmModalConfiguration({
            message: '¿Deseas liberar los lugares de Jugador 1 y Jugador 2? Cualquier participante actualmente conectado será desconectado de inmediato.',
            yesButtonText: 'Sí, liberar lugares',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                setIsConfirmModalOpen(false);
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Liberando lugares de los jugadores...' });
                const res = await ClearPlayers(activeRoomId);
                if (res.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Lugares de jugadores liberados exitosamente.' });
                    if (res.data) updateMemoryStateFromPacket(res.data);
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al liberar lugares.' });
                }
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const handleConfirmChangePin = () => {
        setConfirmModalConfiguration({
            message: '¿Deseas generar un nuevo PIN de acceso? Los participantes actualmente conectados serán desconectados inmediatamente y el código QR cambiará.',
            yesButtonText: 'Sí, generar nuevo PIN',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                setIsConfirmModalOpen(false);
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando nuevo PIN de acceso...' });
                const res = await ChangeAccessPin(activeRoomId);
                if (res.success) {
                    const newPin = res.data?.accessPin || '';
                    if (newPin) setAccessPin(newPin);
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Nuevo PIN generado: ${newPin}. Participantes anteriores desconectados.` });
                    if (res.data) updateMemoryStateFromPacket(res.data);

                    const currentHostname = window.location.hostname;
                    const currentPort = window.location.port ? `:${window.location.port}` : '';
                    const currentProtocol = window.location.protocol;
                    const token = encodeAccessCode(activeRoomId, newPin);
                    setFanJoinQrUrl(`${currentProtocol}//${currentHostname}${currentPort}/memory?t=${token}`);
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar PIN.' });
                }
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // ==========================================
    // MATCH CONTROL ACTIONS
    // ==========================================

    const handleStartMatchAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Iniciando partida con conteo 3..2..1...' });
        const startResponse = await StartMatch(activeRoomId, {
            roundDurationSeconds: roomConfigState.roundDurationSeconds,
            pairsCount: roomConfigState.pairsCount,
            tieBreakMode: roomConfigState.tieBreakMode,
            previewTimeSeconds: roomConfigState.previewTimeSeconds,
            hideUnmatchedDelayMs: roomConfigState.hideUnmatchedDelayMs,
            themeId: selectedThemeId > 0 ? selectedThemeId : null
        });
        if (startResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Secuencia de partida iniciada!' });
            if (startResponse.data) updateMemoryStateFromPacket(startResponse.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: startResponse.message || 'Error al iniciar partida.' });
        }
    };

    const handleConfirmFinishEarly = () => {
        setConfirmModalConfiguration({
            message: '¿Estás seguro de finalizar la partida antes de tiempo? Se evaluará ganador inmediato con los pares actuales.',
            yesButtonText: 'Sí, finalizar ahora',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Finalizando partida anticipadamente...' });
                const finishResponse = await FinishMatchEarly(activeRoomId, null, 'admin_forced');
                if (finishResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida finalizada anticipadamente.' });
                    if (finishResponse.data) updateMemoryStateFromPacket(finishResponse.data);
                    await loadMatchesHistory();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: finishResponse.message || 'Error al finalizar partida.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const handleResetMatchAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando partida y barajando nuevos tableros...' });
        const resetResponse = await ResetMatch(activeRoomId);
        if (resetResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida reiniciada a Escenario Listo.' });
            if (resetResponse.data) updateMemoryStateFromPacket(resetResponse.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: resetResponse.message || 'Error al reiniciar partida.' });
        }
    };

    // ==========================================
    // ON-THE-FLY NAMES UPDATE & EMERGENCY ADJUSTS
    // ==========================================

    const handleEmitPlayerNamesUpdate = async () => {
        if (!activeRoomId) return;
        setIsUpdatingNames(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando nombres de jugadores al vuelo...' });
        try {
            const res = await UpdatePlayerNames(activeRoomId, player1LiveName, player2LiveName);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Nombres actualizados en todas las pantallas.' });
                if (res.data) updateMemoryStateFromPacket(res.data);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar nombres.' });
            }
        } catch (e) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al emitir nombres.' });
        } finally {
            setIsUpdatingNames(false);
        }
    };

    const handleAdjustScoreAction = async (playerSide, delta) => {
        if (!activeRoomId) return;
        const deltaLabel = delta > 0 ? `+${delta}` : `${delta}`;
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Ajustando puntaje (${deltaLabel} pt)...` });
        try {
            const res = await AdjustScore(activeRoomId, playerSide, delta);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Puntaje ajustado (${deltaLabel} pt).` });
                if (res.data) updateMemoryStateFromPacket(res.data);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al ajustar puntaje.' });
            }
        } catch (e) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al ejecutar ajuste de puntaje.' });
        }
    };

    // ==========================================
    // THEME CRUD ACTIONS
    // ==========================================

    const handleOpenCreateThemeModal = () => {
        setThemeToEditObject(null);
        setIsThemeModalOpen(true);
    };

    const handleOpenEditThemeModal = () => {
        const foundTheme = availableThemesList.find((item) => item.id === selectedThemeId);
        setThemeToEditObject(foundTheme || null);
        setIsThemeModalOpen(true);
    };

    const handleCloneSelectedTheme = async () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Clonando tema de Memorama...' });
        try {
            const cloneResponse = await CloneTheme(selectedThemeId, null, activeRoomId);
            if (cloneResponse.success && cloneResponse.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema clonado exitosamente.' });
                await loadAllInitialAdminData();
                setSelectedThemeId(cloneResponse.data.id);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: cloneResponse.message || 'Error al clonar tema.' });
            }
        } catch (cloneError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al clonar tema.' });
        }
    };

    const handleConfirmDeleteTheme = () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        const currentTheme = availableThemesList.find((t) => t.id === selectedThemeId);
        const themeName = currentTheme ? currentTheme.name : 'este tema';

        setConfirmModalConfiguration({
            message: `¿Estás seguro de que deseas eliminar permanentemente el tema "${themeName}"?`,
            yesButtonText: 'Sí, eliminar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando tema...' });
                const deleteResponse = await DeleteTheme(selectedThemeId, activeRoomId);
                if (deleteResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema eliminado correctamente.' });
                    await loadAllInitialAdminData();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: deleteResponse.message || 'Error al eliminar tema.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    /**
     * Renders a human-readable game phase badge.
     */
    const renderGamePhaseBadge = () => {
        const phaseKey = currentMemoryGameState.phase;

        switch (phaseKey) {
            case 'idle':
                return <span className="badge bg-secondary fs-6 px-3 py-2">En Espera (Idle)</span>;
            case 'cover':
                return <span className="badge bg-info text-dark fs-6 px-3 py-2">🖼️ Proyectando Portada</span>;
            case 'game_ready':
                return <span className="badge bg-primary fs-6 px-3 py-2">🏟️ Escenario Listo</span>;
            case 'countdown':
                return (
                    <span className="badge bg-warning text-dark fs-6 px-3 py-2 animate-pulse">
                        ⏳ Cuenta Regresiva: {countdownRemaining ?? 3}s
                    </span>
                );
            case 'preview':
                return (
                    <span className="badge bg-warning text-dark fs-6 px-3 py-2 animate-pulse">
                        👁️ Memorización Previa
                    </span>
                );
            case 'playing':
                return <span className="badge bg-danger fs-6 px-3 py-2 animate-pulse">🃏 ¡PARTIDA EN CURSO!</span>;
            case 'finished':
                return (
                    <span className="badge bg-success fs-6 px-3 py-2">
                        🏆 ¡Finalizado! (Ganador: {currentMemoryGameState.winner || 'Empate'})
                    </span>
                );
            default:
                return <span className="badge bg-secondary fs-6 px-3 py-2">{phaseKey}</span>;
        }
    };

    // Calculate score percentage and timer semaphore
    const totalPairsConfigured = currentMemoryGameState.pairsCount || roomConfigState.pairsCount || 6;
    const p1PairsFound = currentMemoryGameState.player1?.score || 0;
    const p2PairsFound = currentMemoryGameState.player2?.score || 0;

    const percentageP1 = Math.min(100, Math.round((p1PairsFound / totalPairsConfigured) * 100));
    const percentageP2 = Math.min(100, Math.round((p2PairsFound / totalPairsConfigured) * 100));

    const totalRoundDuration = currentMemoryGameState.roundDurationSeconds || roomConfigState.roundDurationSeconds || 60;
    const remainingTime = currentMemoryGameState.remainingSeconds !== undefined ? currentMemoryGameState.remainingSeconds : totalRoundDuration;
    const timerRatio = Math.max(0, Math.min(1, remainingTime / totalRoundDuration));

    const timerColorClass = timerRatio > 0.5 ? 'text-success' : (timerRatio > 0.2 ? 'text-warning' : 'text-danger');
    const timerProgressBgClass = timerRatio > 0.5 ? 'bg-success' : (timerRatio > 0.2 ? 'bg-warning' : 'bg-danger');

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>


            {/* Modal de Códigos QR y Enlaces */}
            {isQrModalOpen && (
                <>
                    <div className="cModal__Background" onClick={() => setIsQrModalOpen(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999 }}>
                        <div
                            className="cModal__Container__Content position-relative p-0 overflow-hidden"
                            style={{ background: 'transparent', boxShadow: 'none', maxWidth: '420px' }}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-dark position-absolute top-0 end-0 m-3 rounded-circle p-2"
                                onClick={() => setIsQrModalOpen(false)}
                                style={{ zIndex: 20 }}
                            >
                                <i className="bi bi-x-lg text-white"></i>
                            </button>
                            <div className="bg-dark text-white p-3 rounded-top-4 text-center border-bottom border-secondary">
                                <span className="d-block small text-warning mb-1">
                                    <i className="bi bi-tv me-1"></i> Pantalla Gigante Estadio / Overlay:
                                </span>
                                <a
                                    href={screenTargetDisplayUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="fw-bold text-info small text-break text-decoration-none d-block"
                                >
                                    {screenTargetDisplayUrl} <i className="bi bi-box-arrow-up-right ms-1"></i>
                                </a>
                            </div>
                            <PremiumQR url={fanJoinQrUrl} title="MEMORAMA 1 VS 1" />

                            {/* Tarjeta de PIN de Acceso */}
                            <div className="bg-dark text-white p-3 border-top border-secondary text-center">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="text-start">
                                        <span className="small text-white-50 d-block">PIN de Acceso:</span>
                                        <span className="fs-3 fw-bold text-warning" style={{ letterSpacing: '4px' }}>
                                            {accessPin || '----'}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-outline-warning btn-sm d-flex align-items-center gap-1"
                                        onClick={handleConfirmChangePin}
                                        title="Generar nuevo PIN y desconectar participantes anteriores"
                                    >
                                        <i className="bi bi-dice-5-fill"></i>
                                        <span>Nuevo PIN</span>
                                    </button>
                                </div>
                                <span className="text-white-50 d-block" style={{ fontSize: '0.78rem' }}>
                                    <i className="bi bi-shield-check text-success me-1"></i> El QR ya incluye la clave automáticamente.
                                </span>
                            </div>

                            {/* Botón Liberar Lugares */}
                            <div className="p-3 bg-black border-top border-secondary rounded-bottom-4">
                                <button
                                    type="button"
                                    className="btn btn-danger w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                                    onClick={handleConfirmClearPlayers}
                                >
                                    <i className="bi bi-person-x-fill fs-5"></i>
                                    <span className="fw-bold">Liberar Lugares de Jugadores</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Modal de Gestión de Temas */}
            {isThemeModalOpen && (
                <MemoryThemeModal
                    isOpen={isThemeModalOpen}
                    initialThemeData={themeToEditObject}
                    roomId={activeRoomId}
                    onCloseModal={() => setIsThemeModalOpen(false)}
                    onThemeSavedSuccessfully={(savedTheme) => {
                        loadAllInitialAdminData();
                        if (savedTheme?.id) setSelectedThemeId(savedTheme.id);
                    }}
                />
            )}

            {/* Modal de Confirmación (Renderizado por encima de cualquier otro modal) */}
            {isConfirmModalOpen && (
                <Confirm
                    message={confirmModalConfiguration.message}
                    actionToYesButton={confirmModalConfiguration.actionToExecute}
                    textYesButton={confirmModalConfiguration.yesButtonText}
                    textNoButton={confirmModalConfiguration.noButtonText}
                    setShowModal={setIsConfirmModalOpen}
                    zIndex={100000}
                />
            )}

            {/* HEADER DEL PANEL */}
            <div className="mb-4 text-center text-dark">
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <i className="bi bi-grid-3x3-gap-fill text-warning fs-1"></i>
                    <h2 className="text-uppercase fw-bold m-0" style={{ letterSpacing: '2px' }}>
                        Memorama 1 vs 1
                    </h2>
                </div>
                <div className="d-inline-flex align-items-center gap-3 bg-white px-3 py-1 rounded-pill border shadow-sm mt-2 flex-wrap">
                    <span className="small">
                        {webSocketConnectionStatus === 'connected' && <strong className="text-success">🟢 En Línea</strong>}
                        {webSocketConnectionStatus === 'connecting' && <strong className="text-warning">🟡 Conectando...</strong>}
                        {webSocketConnectionStatus === 'reconnecting' && <strong className="text-warning">🟡 Reconectando...</strong>}
                        {webSocketConnectionStatus === 'error' && <strong className="text-danger">🔴 Desconectado</strong>}
                    </span>
                    <span className="small text-muted border-start ps-3">
                        Actualizado: <strong className="text-dark">{lastRefreshTimestampString}</strong>
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-light border rounded-circle p-1 d-flex align-items-center justify-content-center"
                        onClick={loadAllInitialAdminData}
                        title="Refrescar estado"
                        style={{ width: '28px', height: '28px' }}
                    >
                        <i className={`bi bi-arrow-repeat text-primary ${isLoadingInitialData ? 'spinner-border spinner-border-sm border-0' : ''}`}></i>
                    </button>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 1: TARJETAS DE PROYECCIÓN Y CONTROL DE PARTIDA         */}
            {/* ============================================================ */}
            <div className="row g-4 mb-4">
                {/* TARJETA 1: PROYECCIÓN Y ENLACES */}
                <div className="col-lg-5">
                    <div className="card shadow-sm border-0 rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                            <h5 className="m-0 fw-bold">
                                <i className="bi bi-projector-fill text-warning me-2"></i> 1. Proyección Pantalla Gigante
                            </h5>
                            <span className="badge bg-primary">Vistas</span>
                        </div>
                        <div className="card-body bg-light d-flex flex-column justify-content-between p-4">
                            <p className="small text-muted mb-3">
                                Alterna instantáneamente la proyección de la pantalla gigante (/screen) entre la Portada Promocional y el Escenario de Memorama.
                            </p>
                            <div className="d-grid gap-3">
                                <button
                                    type="button"
                                    className="btn btn-info fw-bold py-2 text-dark shadow-sm"
                                    onClick={handleShowCoverAction}
                                >
                                    <i className="bi bi-image me-1"></i> Mostrar Portada (Modo 46)
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary fw-bold py-2 text-white shadow-sm"
                                    onClick={handleShowGameSceneAction}
                                >
                                    <i className="bi bi-tv-fill me-1"></i> Proyectar Pantalla de Juego (Modo 46.1)
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-dark fw-bold py-2 shadow-sm"
                                    onClick={handleOpenQrModal}
                                >
                                    <i className="bi bi-qr-code-scan me-1"></i> Ver QR / Enlaces (/memory)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TARJETA 2: CONFIGURACIÓN DE PARTIDA */}
                <div className="col-lg-7">
                    <div className="card shadow-sm border-0 rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                            <h5 className="m-0 fw-bold">
                                <i className="bi bi-gear-wide-connected text-danger me-2"></i> 2. Configuración de Partida
                            </h5>
                            {renderGamePhaseBadge()}
                        </div>
                        <div className="card-body bg-light p-4">
                            {/* Selector Obligatorio de Tema */}
                            <div className="mb-3">
                                <label className="form-label fw-bold small text-muted">
                                    <i className="bi bi-palette-fill me-1 text-primary"></i> Tema Visual de Memorama *:
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                    <select
                                        className="form-select fw-bold border-primary shadow-sm"
                                        value={selectedThemeId || ''}
                                        onChange={handleActiveThemeSelectionChange}
                                        disabled={
                                            currentMemoryGameState.phase === 'playing' ||
                                            currentMemoryGameState.phase === 'countdown' ||
                                            currentMemoryGameState.phase === 'preview'
                                        }
                                    >
                                        <option value="">-- Selecciona un tema obligatorio --</option>
                                        {availableThemesList.map((themeOption) => (
                                            <option key={themeOption.id} value={themeOption.id}>
                                                {themeOption.name}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary fw-bold text-nowrap"
                                        onClick={handleOpenEditThemeModal}
                                        disabled={!selectedThemeId || selectedThemeId <= 0}
                                        title="Editar tema seleccionado"
                                    >
                                        <i className="bi bi-pencil-square"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success fw-bold text-nowrap"
                                        onClick={handleOpenCreateThemeModal}
                                        title="Crear nuevo tema"
                                    >
                                        <i className="bi bi-plus-lg"></i>
                                    </button>
                                </div>
                            </div>

                            {/* Selectores de duración, cantidad de pares y criterio de desempate */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-muted">
                                        <i className="bi bi-clock-history me-1 text-primary"></i> Duración de Ronda:
                                    </label>
                                    <select
                                        className="form-select fw-bold"
                                        value={roomConfigState.roundDurationSeconds}
                                        onChange={(e) => handleConfigParameterChange('roundDurationSeconds', Number(e.target.value))}
                                        disabled={
                                            currentMemoryGameState.phase === 'playing' ||
                                            currentMemoryGameState.phase === 'countdown' ||
                                            currentMemoryGameState.phase === 'preview'
                                        }
                                    >
                                        <option value={15}>15 Segundos (Flash)</option>
                                        <option value={30}>30 Segundos</option>
                                        <option value={45}>45 Segundos</option>
                                        <option value={60}>60 Segundos (Recomendado)</option>
                                        <option value={90}>90 Segundos</option>
                                        <option value={120}>120 Segundos (2 min)</option>
                                        <option value={180}>180 Segundos (3 min)</option>
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-muted">
                                        <i className="bi bi-grid-3x3 me-1 text-primary"></i> Cantidad de Pares:
                                    </label>
                                    <select
                                        className="form-select fw-bold border-info shadow-sm"
                                        value={roomConfigState.pairsCount}
                                        onChange={(e) => handleConfigParameterChange('pairsCount', Number(e.target.value))}
                                        disabled={
                                            currentMemoryGameState.phase === 'playing' ||
                                            currentMemoryGameState.phase === 'countdown' ||
                                            currentMemoryGameState.phase === 'preview'
                                        }
                                    >
                                        <option value={4}>4 Pares (8 cartas - Rápido)</option>
                                        <option value={6}>6 Pares (12 cartas - Estándar)</option>
                                        <option value={8}>8 Pares (16 cartas - Desafío)</option>
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-muted">
                                        <i className="bi bi-trophy-fill text-warning me-1"></i> Desempate:
                                    </label>
                                    <select
                                        className="form-select fw-bold border-warning shadow-sm"
                                        value={roomConfigState.tieBreakMode}
                                        onChange={(e) => handleConfigParameterChange('tieBreakMode', e.target.value)}
                                        disabled={
                                            currentMemoryGameState.phase === 'playing' ||
                                            currentMemoryGameState.phase === 'countdown' ||
                                            currentMemoryGameState.phase === 'preview'
                                        }
                                    >
                                        <option value="least_flips">Menos Movimientos (Flips)</option>
                                        <option value="pure_tie">Empate Puro (Tie)</option>
                                        <option value="fastest_match">Primer Par Acertado (Speed)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Acordeón de Opciones Avanzadas */}
                            <div className="card border mb-3 shadow-sm rounded-3 overflow-hidden">
                                <button
                                    type="button"
                                    className="btn btn-light w-100 text-start d-flex justify-content-between align-items-center p-3 fw-bold small text-dark border-0 shadow-none"
                                    onClick={() => setIsAdvancedOptionsOpen((prev) => !prev)}
                                    style={{ background: '#f8f9fa' }}
                                >
                                    <span>
                                        <i className="bi bi-sliders text-primary me-2"></i>
                                        Opciones Avanzadas (Tiempos de Memorización y Volteo)
                                    </span>
                                    <i className={`bi bi-chevron-${isAdvancedOptionsOpen ? 'up' : 'down'} text-muted`}></i>
                                </button>
                                {isAdvancedOptionsOpen && (
                                    <div className="p-3 bg-white border-top">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label small text-muted fw-bold mb-1">
                                                    <i className="bi bi-eye-fill text-primary me-1"></i>
                                                    Tiempo de Memorización Previa (Preview Time):
                                                </label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <select
                                                        className="form-select form-select-sm fw-bold"
                                                        value={roomConfigState.previewTimeSeconds}
                                                        onChange={(e) => handleConfigParameterChange('previewTimeSeconds', Number(e.target.value))}
                                                        disabled={
                                                            currentMemoryGameState.phase === 'playing' ||
                                                            currentMemoryGameState.phase === 'countdown' ||
                                                            currentMemoryGameState.phase === 'preview'
                                                        }
                                                    >
                                                        <option value={0}>0s (Sin Memorización)</option>
                                                        <option value={2}>2 Segundos</option>
                                                        <option value={3}>3 Segundos (Recomendado)</option>
                                                        <option value={4}>4 Segundos</option>
                                                        <option value={5}>5 Segundos</option>
                                                    </select>
                                                </div>
                                                <small className="text-muted d-block mt-1">
                                                    Muestra todas las cartas al derecho al terminar el conteo 3..2..1.
                                                </small>
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label small text-muted fw-bold mb-1">
                                                    <i className="bi bi-hourglass-split text-danger me-1"></i>
                                                    Retardo de Ocultamiento de Fallos (Hide Delay):
                                                </label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <select
                                                        className="form-select form-select-sm fw-bold"
                                                        value={roomConfigState.hideUnmatchedDelayMs}
                                                        onChange={(e) => handleConfigParameterChange('hideUnmatchedDelayMs', Number(e.target.value))}
                                                        disabled={
                                                            currentMemoryGameState.phase === 'playing' ||
                                                            currentMemoryGameState.phase === 'countdown' ||
                                                            currentMemoryGameState.phase === 'preview'
                                                        }
                                                    >
                                                        <option value={500}>500 ms (Rápido)</option>
                                                        <option value={800}>800 ms (Estándar)</option>
                                                        <option value={1000}>1000 ms (1 segundo)</option>
                                                        <option value={1500}>1500 ms (Lento)</option>
                                                    </select>
                                                </div>
                                                <small className="text-muted d-block mt-1">
                                                    Tiempo que permanecen descubiertas dos cartas no coincidentes.
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Botones de Acción de Partida */}
                            <div className="row g-2">
                                <div className="col-md-5">
                                    <button
                                        type="button"
                                        className="btn btn-success fw-bold w-100 py-2 shadow-sm text-white"
                                        onClick={handleStartMatchAction}
                                        disabled={
                                            currentMemoryGameState.phase === 'playing' ||
                                            currentMemoryGameState.phase === 'countdown' ||
                                            currentMemoryGameState.phase === 'preview'
                                        }
                                    >
                                        <i className="bi bi-play-fill fs-5 me-1"></i> Iniciar Partida (3, 2, 1)
                                    </button>
                                </div>
                                <div className="col-md-4">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger fw-bold w-100 py-2 shadow-sm"
                                        onClick={handleConfirmFinishEarly}
                                        disabled={
                                            currentMemoryGameState.phase !== 'playing' &&
                                            currentMemoryGameState.phase !== 'preview'
                                        }
                                    >
                                        <i className="bi bi-stop-circle-fill me-1"></i> Finalizar Antes
                                    </button>
                                </div>
                                <div className="col-md-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary fw-bold w-100 py-2 shadow-sm"
                                        onClick={handleResetMatchAction}
                                        title="Reiniciar tableros y devolver a Escenario Listo"
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-1"></i> Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 2: MONITOR DE TELEMETRÍA EN VIVO Y NOMBRES AL VUELO    */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-activity text-success me-2"></i> 3. Monitor de Telemetría en Vivo y Edición de Nombres al Vuelo
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-secondary">
                            Pares Objetivo: <strong className="text-warning">{totalPairsConfigured}</strong>
                        </span>
                        <span className="badge bg-secondary">
                            Desempate: <strong className="text-info">{roomConfigState.tieBreakMode}</strong>
                        </span>
                    </div>
                </div>
                <div className="card-body bg-white p-4">
                    {/* Timer Central y Semáforo de Tiempo */}
                    <div className="text-center mb-4">
                        <span className="small text-uppercase fw-bold text-muted d-block">Tiempo Restante</span>
                        <h1 className={`fw-bold display-4 m-0 font-monospace ${timerColorClass}`}>
                            {remainingTime?.toFixed(1)}s
                        </h1>
                        <div className="progress mt-2 mx-auto" style={{ height: '8px', maxWidth: '420px' }}>
                            <div
                                className={`progress-bar progress-bar-striped progress-bar-animated ${timerProgressBgClass}`}
                                style={{ width: `${timerRatio * 100}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Comparativa J1 vs J2 con Edición de Nombres al Vuelo */}
                    <div className="row align-items-stretch g-3">
                        {/* JUGADOR 1 (SLOT IZQUIERDO) */}
                        <div className="col-md-5">
                            <div
                                className="p-3 rounded-4 border shadow-sm h-100 d-flex flex-column justify-content-between"
                                style={{
                                    borderLeft: `8px solid ${activeThemeObject?.player1Color || currentMemoryGameState.player1?.color || '#fbbf24'}`,
                                    background: '#f8fafc'
                                }}
                            >
                                <div>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <h5
                                                className="fw-bold m-0"
                                                style={{ color: activeThemeObject?.player1Color || currentMemoryGameState.player1?.color || '#d97706' }}
                                            >
                                                <i className="bi bi-person-circle me-1"></i> Jugador 1
                                            </h5>
                                        </div>
                                        <div>
                                            {currentMemoryGameState.player1?.isConnected ? (
                                                <span className="badge bg-success-subtle text-success border border-success fw-bold">
                                                    🟢 Conectado
                                                </span>
                                            ) : (
                                                <span className="badge bg-secondary-subtle text-secondary border fw-normal">
                                                    ⚪ Esperando conexión
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Input en vivo para Jugador 1 */}
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted mb-1">
                                            Nombre al vuelo (J1):
                                        </label>
                                        <div className="input-group input-group-sm">
                                            <input
                                                type="text"
                                                className="form-control fw-bold"
                                                value={player1LiveName}
                                                onChange={(e) => setPlayer1LiveName(e.target.value)}
                                                onBlur={handleEmitPlayerNamesUpdate}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleEmitPlayerNamesUpdate();
                                                }}
                                                placeholder="Nombre J1"
                                            />
                                            <button
                                                className="btn btn-outline-primary fw-bold"
                                                type="button"
                                                onClick={handleEmitPlayerNamesUpdate}
                                                disabled={isUpdatingNames}
                                                title="Emitir actualización de nombres a pantallas"
                                            >
                                                <i className="bi bi-send-fill"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-3 my-2">
                                        {(activeThemeObject?.player1Avatar || currentMemoryGameState.player1?.avatarUrl) && (
                                            <img
                                                src={resolveMediaUrl(activeThemeObject?.player1Avatar || currentMemoryGameState.player1?.avatarUrl)}
                                                alt="J1 Avatar"
                                                className="rounded-circle border p-1 bg-white shadow-sm"
                                                style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        )}
                                        <div>
                                            <span className="small text-muted d-block">Pares Encontrados:</span>
                                            <h3 className="fw-bold m-0 text-dark">
                                                {p1PairsFound} <span className="fs-6 text-muted">/ {totalPairsConfigured} pares</span>
                                            </h3>
                                            <span className="small text-secondary">
                                                Movimientos (Flips): <strong>{currentMemoryGameState.player1?.totalFlips || 0}</strong>
                                            </span>
                                        </div>
                                    </div>

                                    <div className="progress mt-2" style={{ height: '14px' }}>
                                        <div
                                            className="progress-bar fw-bold"
                                            style={{
                                                width: `${percentageP1}%`,
                                                backgroundColor: activeThemeObject?.player1Color || currentMemoryGameState.player1?.color || '#fbbf24'
                                            }}
                                        >
                                            {percentageP1 > 15 && `${percentageP1}%`}
                                        </div>
                                    </div>
                                </div>

                                {/* Controles de Emergencia J1 */}
                                <div className="mt-3 pt-2 border-top d-flex justify-content-between align-items-center">
                                    <span className="small text-muted fw-bold">Ajuste de Emergencia:</span>
                                    <div className="btn-group btn-group-sm">
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger fw-bold"
                                            onClick={() => handleAdjustScoreAction('player1', -1)}
                                            title="Restar 1 punto a Jugador 1"
                                            disabled={p1PairsFound <= 0}
                                        >
                                            -1 Pt
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-success fw-bold"
                                            onClick={() => handleAdjustScoreAction('player1', 1)}
                                            title="Sumar 1 punto a Jugador 1"
                                        >
                                            +1 Pt
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* VS CENTRAL */}
                        <div className="col-md-2 text-center d-flex flex-column justify-content-center align-items-center">
                            <span className="badge bg-dark fs-4 px-3 py-2 rounded-pill shadow mb-2">VS</span>
                            <span className="small text-muted fw-bold">
                                {currentMemoryGameState.player1?.totalFlips + currentMemoryGameState.player2?.totalFlips} Flips Totales
                            </span>
                        </div>

                        {/* JUGADOR 2 (SLOT DERECHO) */}
                        <div className="col-md-5">
                            <div
                                className="p-3 rounded-4 border shadow-sm h-100 d-flex flex-column justify-content-between"
                                style={{
                                    borderRight: `8px solid ${activeThemeObject?.player2Color || currentMemoryGameState.player2?.color || '#60a5fa'}`,
                                    background: '#f8fafc'
                                }}
                            >
                                <div>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div>
                                            {currentMemoryGameState.player2?.isConnected ? (
                                                <span className="badge bg-success-subtle text-success border border-success fw-bold">
                                                    🟢 Conectado
                                                </span>
                                            ) : (
                                                <span className="badge bg-secondary-subtle text-secondary border fw-normal">
                                                    ⚪ Esperando conexión
                                                </span>
                                            )}
                                        </div>
                                        <h5
                                            className="fw-bold m-0"
                                            style={{ color: activeThemeObject?.player2Color || currentMemoryGameState.player2?.color || '#2563eb' }}
                                        >
                                            Jugador 2 <i className="bi bi-person-circle ms-1"></i>
                                        </h5>
                                    </div>

                                    {/* Input en vivo para Jugador 2 */}
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted mb-1 text-end d-block">
                                            Nombre al vuelo (J2):
                                        </label>
                                        <div className="input-group input-group-sm">
                                            <button
                                                className="btn btn-outline-primary fw-bold"
                                                type="button"
                                                onClick={handleEmitPlayerNamesUpdate}
                                                disabled={isUpdatingNames}
                                                title="Emitir actualización de nombres a pantallas"
                                            >
                                                <i className="bi bi-send-fill"></i>
                                            </button>
                                            <input
                                                type="text"
                                                className="form-control fw-bold text-end"
                                                value={player2LiveName}
                                                onChange={(e) => setPlayer2LiveName(e.target.value)}
                                                onBlur={handleEmitPlayerNamesUpdate}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleEmitPlayerNamesUpdate();
                                                }}
                                                placeholder="Nombre J2"
                                            />
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center justify-content-end gap-3 my-2 text-end">
                                        <div>
                                            <span className="small text-muted d-block">Pares Encontrados:</span>
                                            <h3 className="fw-bold m-0 text-dark">
                                                {p2PairsFound} <span className="fs-6 text-muted">/ {totalPairsConfigured} pares</span>
                                            </h3>
                                            <span className="small text-secondary">
                                                Movimientos (Flips): <strong>{currentMemoryGameState.player2?.totalFlips || 0}</strong>
                                            </span>
                                        </div>
                                        {(activeThemeObject?.player2Avatar || currentMemoryGameState.player2?.avatarUrl) && (
                                            <img
                                                src={resolveMediaUrl(activeThemeObject?.player2Avatar || currentMemoryGameState.player2?.avatarUrl)}
                                                alt="J2 Avatar"
                                                className="rounded-circle border p-1 bg-white shadow-sm"
                                                style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div className="progress mt-2" style={{ height: '14px' }}>
                                        <div
                                            className="progress-bar fw-bold ms-auto"
                                            style={{
                                                width: `${percentageP2}%`,
                                                backgroundColor: activeThemeObject?.player2Color || currentMemoryGameState.player2?.color || '#60a5fa'
                                            }}
                                        >
                                            {percentageP2 > 15 && `${percentageP2}%`}
                                        </div>
                                    </div>
                                </div>

                                {/* Controles de Emergencia J2 */}
                                <div className="mt-3 pt-2 border-top d-flex justify-content-between align-items-center">
                                    <div className="btn-group btn-group-sm">
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger fw-bold"
                                            onClick={() => handleAdjustScoreAction('player2', -1)}
                                            title="Restar 1 punto a Jugador 2"
                                            disabled={p2PairsFound <= 0}
                                        >
                                            -1 Pt
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-success fw-bold"
                                            onClick={() => handleAdjustScoreAction('player2', 1)}
                                            title="Sumar 1 punto a Jugador 2"
                                        >
                                            +1 Pt
                                        </button>
                                    </div>
                                    <span className="small text-muted fw-bold">Ajuste de Emergencia:</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 3: GESTOR DE TEMAS Y CATÁLOGO                          */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-palette-fill text-info me-2"></i> 4. Gestor de Temas y Barajas de Cartas
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-success fw-bold"
                        onClick={handleOpenCreateThemeModal}
                    >
                        <i className="bi bi-plus-circle me-1"></i> Crear nuevo tema
                    </button>
                </div>
                <div className="card-body bg-light p-4">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Temas Disponibles:</label>
                            <select
                                className="form-select fw-bold border-primary shadow-sm"
                                value={selectedThemeId || ''}
                                onChange={handleActiveThemeSelectionChange}
                            >
                                <option value="">-- Selecciona un tema --</option>
                                {availableThemesList.map((themeItem) => (
                                    <option key={themeItem.id} value={themeItem.id}>
                                        {themeItem.name} {themeItem.id === selectedThemeId ? '⭐ (Activo)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6 d-flex gap-2 justify-content-md-end flex-wrap pt-md-4">
                            <button
                                type="button"
                                className="btn btn-primary fw-bold shadow-sm"
                                onClick={handleOpenEditThemeModal}
                                disabled={!selectedThemeId || selectedThemeId <= 0}
                            >
                                <i className="bi bi-pencil-square me-1"></i> Editar Tema
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold shadow-sm"
                                onClick={handleCloneSelectedTheme}
                                disabled={!selectedThemeId || selectedThemeId <= 0}
                            >
                                <i className="bi bi-copy me-1"></i> Clonar
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-danger fw-bold shadow-sm"
                                onClick={handleConfirmDeleteTheme}
                                disabled={!selectedThemeId || selectedThemeId <= 0 || availableThemesList.length <= 1}
                            >
                                <i className="bi bi-trash-fill me-1"></i> Eliminar
                            </button>
                        </div>
                    </div>

                    {/* Previsualización del Tema Activo */}
                    {activeThemeObject && (
                        <div className="mt-3 p-3 bg-white rounded-3 border shadow-sm">
                            <div className="row g-2 align-items-center">
                                <div className="col-auto">
                                    {activeThemeObject.brandLogoUrl && (
                                        <img
                                            src={resolveMediaUrl(activeThemeObject.brandLogoUrl)}
                                            alt="Logo Brand"
                                            style={{ maxHeight: '40px' }}
                                        />
                                    )}
                                    {activeThemeObject.sponsorLogoUrl && (
                                        <img
                                            src={resolveMediaUrl(activeThemeObject.sponsorLogoUrl)}
                                            alt="Logo Sponsor"
                                            className="ms-2"
                                            style={{ maxHeight: '40px' }}
                                        />
                                    )}
                                </div>
                                <div className="col">
                                    <strong className="text-dark">{activeThemeObject.name}</strong>
                                    <div className="small text-muted">
                                        Portada: {activeThemeObject.coverTitle || 'MEMORAMA 1 VS 1'} | Tipografía: {activeThemeObject.fontFamily || 'Fredoka'} | Colores:{' '}
                                        <span className="badge" style={{ backgroundColor: activeThemeObject.player1Color || '#fbbf24' }}>
                                            {activeThemeObject.player1Name || 'J1'}
                                        </span>{' '}
                                        <span className="badge" style={{ backgroundColor: activeThemeObject.player2Color || '#60a5fa' }}>
                                            {activeThemeObject.player2Name || 'J2'}
                                        </span>{' '}
                                        | Modo de Baraja: {activeThemeObject.useSameDeckForAll ? 'Simétrico' : 'Asimétrico'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 4: HISTORIAL DE PARTIDAS DISPUTADAS                    */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-clock-history text-secondary me-2"></i> 5. Historial de Partidas Disputadas
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-light"
                        onClick={loadMatchesHistory}
                    >
                        <i className="bi bi-arrow-repeat me-1"></i> Actualizar Historial
                    </button>
                </div>
                <div className="card-body bg-white p-0">
                    <div className="table-responsive" style={{ maxHeight: '520px', overflowY: 'auto' }}>
                        <table className="table table-striped table-hover align-middle mb-0">
                            <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                                <tr>
                                    <th>Fecha / Hora</th>
                                    <th>Tema</th>
                                    <th>Ganador</th>
                                    <th>Pares J1</th>
                                    <th>Pares J2</th>
                                    <th>Movimientos (Flips)</th>
                                    <th>Duración</th>
                                    <th>Motivo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matchesHistoryList.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-4 text-muted">
                                            No se han disputado partidas de Memorama en esta sala todavía.
                                        </td>
                                    </tr>
                                ) : (
                                    matchesHistoryList.map((matchItem) => {
                                        const rawWinner = String(matchItem.winner || '').toLowerCase().trim();
                                        const isP1 = rawWinner === 'player1' || rawWinner === 'j1' || rawWinner === 'p1';
                                        const isP2 = rawWinner === 'player2' || rawWinner === 'j2' || rawWinner === 'p2';

                                        return (
                                            <tr key={matchItem.id}>
                                                <td className="small text-muted">
                                                    {matchItem.playedAt ? new Date(matchItem.playedAt).toLocaleString() : '-'}
                                                </td>
                                                <td className="small fw-bold">
                                                    {matchItem.themeName || 'Predeterminado'}
                                                </td>
                                                <td>
                                                    {isP1 ? (
                                                        <span className="badge bg-warning text-dark fw-bold">Jugador 1 🏆</span>
                                                    ) : isP2 ? (
                                                        <span className="badge bg-info text-dark fw-bold">Jugador 2 🏆</span>
                                                    ) : (
                                                        <span className="badge bg-secondary fw-bold">Empate 🤝</span>
                                                    )}
                                                </td>
                                                <td className="fw-bold text-warning">
                                                    {matchItem.scoreJ1} pares
                                                </td>
                                                <td className="fw-bold text-info">
                                                    {matchItem.scoreJ2} pares
                                                </td>
                                                <td className="small">
                                                    J1: {matchItem.totalFlipsJ1} | J2: {matchItem.totalFlipsJ2}
                                                </td>
                                                <td className="small font-monospace">
                                                    {matchItem.effectiveDurationSeconds}s
                                                </td>
                                                <td className="small text-muted">
                                                    {matchItem.winningReason === 'all_pairs_matched'
                                                        ? 'Completó todos los pares'
                                                        : matchItem.winningReason === 'time_expired' || matchItem.winningReason === 'time_up'
                                                        ? 'Tiempo agotado'
                                                        : matchItem.winningReason === 'admin_forced'
                                                        ? 'Finalizado por operador'
                                                        : matchItem.winningReason === 'pure_tie'
                                                        ? 'Empate puro'
                                                        : matchItem.winningReason === 'least_flips'
                                                        ? 'Menos movimientos'
                                                        : matchItem.winningReason === 'fastest_match'
                                                        ? 'Primer par acertado'
                                                        : matchItem.winningReason === 'no_pairs_found'
                                                        ? 'Sin pares descubiertos'
                                                        : (matchItem.winningReason || 'Partida concluida')}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </SecondaryBase>
    );
};

Memory.propTypes = {
    getOutOfTheDynamic: PropTypes.func.isRequired,
    roomId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export const Memorama = Memory;
export default Memory;
